import { ChangeDetectorRef, Component, DestroyRef, Inject, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA,
  MatLegacyDialogRef as MatDialogRef,
} from '@angular/material/legacy-dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatOptionModule } from '@angular/material/core';

import { forkJoin, map } from 'rxjs';
import { TsButtonModule } from 'tsui';
import { TsDialogComponent } from 'tsui/lib/ts-dialog/ts-dialog.component';
import { RelatedToType } from '@shared/enums';
import { AddressPart } from '@shared/models';
import { PropertyType } from '@core/models/account.types';
import { AccountService } from '@core/services/account.service';
import { DirectivesModule } from '@shared/directives/directives.module';
import { PropertiesService } from '@modules/properties/properties.service';
import { ProjectTemplate, Property } from '@modules/projects/projects.types';
import * as CustomValidator from '@shared/directives/custom-validators.directive';
import { LayoutsService } from '@services/layouts.service';

declare interface DialogDataInterface {
  byPass?: boolean;
  model: any;
  multi_select?: boolean;
  related_to_type: RelatedToType;
  searchTerm?: string;
}

@Component({
  selector: 'add-property-modal',
  standalone: true,
  imports: [
    TsDialogComponent,
    TsButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatOptionModule,
    FormsModule,
    ReactiveFormsModule,
    DirectivesModule,
  ],
  templateUrl: './add-property-modal.component.html',
  styleUrls: ['./add-property-modal.component.scss'],
})
export class AddPropertyModalComponent implements OnInit {
  propertyForm: FormGroup;
  propertyTypes: PropertyType[] = [];
  propertyTemplates: ProjectTemplate<any>[] = [];
  isLoading = false;
  isOptionsLoading = true;

  get isSaveBtnDisabled(): boolean {
    return this.isLoading || this.propertyForm.invalid || this.propertyForm.pristine;
  }

  constructor(
    @Inject(MAT_DIALOG_DATA) private readonly data: DialogDataInterface,
    private readonly matDialogRef: MatDialogRef<AddPropertyModalComponent>,
    private readonly formBuilder: FormBuilder,
    private readonly propertiesService: PropertiesService,
    private readonly layoutsService: LayoutsService,
    private readonly accountService: AccountService,
    private readonly changeDetectorRef: ChangeDetectorRef,
    private readonly destroyRef: DestroyRef,
  ) {}

  ngOnInit(): void {
    this.buildForm();
    this.loadOptions();
  }

  private buildForm(): void {
    this.propertyForm = this.formBuilder.group({
      name: [this.data?.searchTerm || '', [Validators.required, CustomValidator.noWhiteSpaceValidator]],
      property_type_id: [null, [Validators.required]],
      template_id: [null],
      address: this.formBuilder.group({
        address_string: ['', [Validators.required, CustomValidator.noWhiteSpaceValidator]],
        street: [''],
        city: [''],
        state: [''],
        postal_code: [''],
        latitude: [''],
        longitude: [''],
      }),
    });
  }

  private loadOptions(): void {
    this.isOptionsLoading = true;
    forkJoin({
      standardDropdowns: this.accountService.getStandardDropdowns(),
      propertyTemplates: this.layoutsService.get<any>(RelatedToType.Property),
    })
      .pipe(
        map(({ standardDropdowns, propertyTemplates }) => {
          const templates = this.layoutsService.extractTemplates(propertyTemplates);

          return {
            propertyTypes: (standardDropdowns?.property_types || []).filter((option) => !option.hidden),
            propertyTemplates: templates.filter((option) => !option.hidden).sort((a, b) => a.rank - b.rank),
          };
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(({ propertyTypes, propertyTemplates }) => {
        this.propertyTypes = propertyTypes;
        this.propertyTemplates = propertyTemplates;
        this.isOptionsLoading = false;
        this.changeDetectorRef.markForCheck();
      });
  }

  setAddress(addressString: string): void {
    this.propertyForm.get('address').get('address_string').setValue(addressString);
  }

  onSelectAddressParts({
    locality,
    administrative_area_level_1,
    street_number,
    route,
    postal_code,
    lat,
    lng,
  }: AddressPart): void {
    this.propertyForm.get('address').get('city').setValue(locality);
    this.propertyForm.get('address').get('state').setValue(administrative_area_level_1);
    this.propertyForm.get('address').get('postal_code').setValue(postal_code);
    this.propertyForm.get('address').get('latitude').setValue(lat);
    this.propertyForm.get('address').get('longitude').setValue(lng);

    if (route) {
      const streetValue = street_number ? `${street_number} ${route}` : route;
      this.propertyForm.get('address').get('street').setValue(streetValue);
    } else {
      this.propertyForm.get('address').get('street').setValue('');
    }
  }

  saveProperty(): void {
    this.propertyForm.disable();
    this.isLoading = true;

    const property = this.propertyForm.value;
    this.propertiesService
      .saveProperty(property)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (createdProperty: Property) => {
          this.matDialogRef.close({
            model: { ...createdProperty, name: createdProperty?.name || `Property ${createdProperty?.id}` },
            multi_select: this.data?.multi_select,
          });
        },
        error: () => {
          this.isLoading = false;
          this.propertyForm.enable();
          this.changeDetectorRef.markForCheck();
        },
      });
  }

  discard(): void {
    this.matDialogRef.close();
  }
}
